import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { Download } from "lucide-react";
import Pagination, { useClientPagination } from "../components/Pagination";
import api from "../lib/api";
import { toast } from "sonner";
import { History, Loader2, Filter, FileSearch } from "lucide-react";

const ACTION_LABELS = {
  login_success: { label: "Inicio de sesión", color: "bg-green-100 text-green-800" },
  login_failed: { label: "Login fallido", color: "bg-red-100 text-red-800" },
  logout: { label: "Cierre de sesión", color: "bg-gray-100 text-gray-700" },
  mfa_enabled: { label: "MFA activado", color: "bg-purple-100 text-purple-800" },
  mfa_disabled: { label: "MFA desactivado", color: "bg-yellow-100 text-yellow-800" },
  mfa_failed: { label: "MFA inválido", color: "bg-red-100 text-red-800" },
  password_changed: { label: "Contraseña cambiada", color: "bg-blue-100 text-blue-800" },
  user_created: { label: "Usuario creado", color: "bg-emerald-100 text-emerald-800" },
  user_updated: { label: "Usuario actualizado", color: "bg-blue-100 text-blue-800" },
  user_unlocked: { label: "Cuenta desbloqueada", color: "bg-orange-100 text-orange-800" },
  order_created: { label: "Orden creada", color: "bg-cyan-100 text-cyan-800" },
  order_transition: { label: "Cambio de estado", color: "bg-indigo-100 text-indigo-800" },
};

export default function AuditLog() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity: "", action: "", limit: 100 });
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.entity) params.entity = filters.entity;
      if (filters.action) params.action = filters.action;
      params.limit = filters.limit;
      const { data } = await api.get("/v1/audit-log", { params });
      setItems(data.items || []);
    } catch {
      toast.error("Error al cargar el log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAudit(); }, []);

  // v2.8.8.8 · Exportar audit log a CSV (requiere M17.audit.export)
  const exportCSV = () => {
    setExporting(true);
    try {
      const header = "id,timestamp,user,entity,entity_id,action,ip,details\n";
      const rows = items.map((it) => {
        const details = (it.details ? JSON.stringify(it.details) : "").replace(/"/g, '""');
        return `"${it.id||''}","${it.timestamp||''}","${it.user_email||it.user_id||''}","${it.entity||''}","${it.entity_id||''}","${it.action||''}","${it.ip||''}","${details}"`;
      }).join("\n");
      const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exportadas ${items.length} entradas`);
    } catch {
      toast.error("Error al exportar");
    } finally { setExporting(false); }
  };

  const pg = useClientPagination(items, 25);

  return (
    <Layout>
      <div className="space-y-6" data-testid="audit-log-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-mono mb-1">Auditoría inmutable</div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Audit Log</h1>
            <p className="text-gray-600 mt-1 text-sm">Registro append-only de todas las acciones del sistema con autor, IP y timestamp.</p>
          </div>
          {hasPerm("M17.audit.export") && (
            <button
              onClick={exportCSV}
              disabled={exporting || items.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-[#0A3747] text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="btn-export-audit"
              title="Exportar audit log (M17.audit.export)"
            >
              <Download size={14} /> {exporting ? "Exportando..." : "Exportar CSV"}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="text-[10px] uppercase tracking-wider text-gray-600 font-mono">Entidad</label>
              <select value={filters.entity} onChange={(e) => setFilters({ ...filters, entity: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded text-sm mt-1">
                <option value="">Todas</option>
                <option value="user">user</option>
                <option value="work_order">work_order</option>
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-[10px] uppercase tracking-wider text-gray-600 font-mono">Acción</label>
              <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded text-sm mt-1">
                <option value="">Todas</option>
                {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="w-32">
              <label className="text-[10px] uppercase tracking-wider text-gray-600 font-mono">Límite</label>
              <select value={filters.limit} onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded text-sm mt-1">
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
            </div>
            <button onClick={fetchAudit} className="px-4 py-2 bg-[#0A3747] text-white rounded text-sm hover:bg-[#135166] flex items-center gap-2" data-testid="audit-apply-filter">
              <Filter size={14} /> Aplicar filtros
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat label="Eventos cargados" value={items.length} />
          <Stat label="Logins exitosos" value={items.filter((i) => i.action === "login_success").length} />
          <Stat label="Eventos críticos" value={items.filter((i) => ["login_failed", "mfa_failed", "user_unlocked"].includes(i.action)).length} />
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0A3747] text-white text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Fecha/Hora</th>
                    <th className="px-4 py-3 font-medium">Acción</th>
                    <th className="px-4 py-3 font-medium">Actor</th>
                    <th className="px-4 py-3 font-medium">Entidad</th>
                    <th className="px-4 py-3 font-medium">IP</th>
                    <th className="px-4 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {pg.paged.map((row) => {
                    const meta = ACTION_LABELS[row.action] || { label: row.action, color: "bg-gray-100 text-gray-800" };
                    return (
                      <tr key={row.id} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`audit-row-${row.id}`}>
                        <td className="px-4 py-2 font-mono text-xs text-gray-600">{new Date(row.at).toLocaleString("es-EC")}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}>{meta.label}</span>
                        </td>
                        <td className="px-4 py-2">
                          <div className="text-xs">{row.actor_email || "—"}</div>
                          <div className="text-[10px] text-gray-400 font-mono">{row.actor_role || ""}</div>
                        </td>
                        <td className="px-4 py-2 text-xs font-mono">{row.entity}/{(row.entity_id || "").slice(0, 8)}</td>
                        <td className="px-4 py-2 text-xs font-mono text-gray-500">{row.ip || "—"}</td>
                        <td className="px-4 py-2">
                          <button onClick={() => setSelected(row)} className="text-xs text-gray-600 hover:text-gray-900">
                            <FileSearch size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">Sin eventos para los filtros aplicados.</td></tr>
                  )}
                </tbody>
              </table>
              {pg.total > 0 && (
                <Pagination
                  page={pg.page}
                  pageSize={pg.pageSize}
                  total={pg.total}
                  onPageChange={pg.setPage}
                  onPageSizeChange={pg.setPageSize}
                  testIdPrefix="audit-pagination"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-[#0A3747] text-white">
              <h2 className="font-semibold">Detalle del evento</h2>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-white/10 rounded">×</button>
            </div>
            <pre className="p-5 text-xs bg-gray-50 overflow-x-auto font-mono">{JSON.stringify(selected, null, 2)}</pre>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-mono">{label}</div>
      <div className="text-2xl font-bold text-[#0A3747] mt-1" style={{ fontFamily: "Chivo" }}>{value}</div>
    </div>
  );
}
